import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Moon, Sun, LogOut, History, Settings, LayoutDashboard, ShieldCheck, BookOpen, Tag, Wand2, ChevronDown, Sparkles, Rocket, ClipboardList } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../lib/auth-context';
import { useTheme } from '../lib/theme';
import { QuotaPill } from './billing/QuotaPill';
import { GuestCheckoutDialog } from './billing/GuestCheckoutDialog';

export function Logo({ className = '' }) {
  return (
    <Link
      to="/"
      className={`flex items-center shrink-0 ${className}`}
      data-testid="app-logo"
      aria-label="Blayzrr — home"
    >
      <img
        src="/blayzrr-wordmark.png"
        alt="Blayzrr"
        width={640}
        height={360}
        className="h-11 w-auto max-w-none dark:invert dark:brightness-200"
        loading="eager"
        decoding="async"
      />
    </Link>
  );
}

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label="Toggle theme"
      data-testid="settings-theme-toggle"
      className="rounded-lg"
    >
      {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </Button>
  );
}

export function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Buy-Now dialog state. Replaces the former "Sign up" button in the
  // guest header — click opens the shared ``GuestCheckoutDialog`` which
  // collects name+email then invokes Razorpay for the ₹999 one-time
  // Full Profile Makeover purchase. On success the webhook auto-
  // provisions the account and emails a one-click login link.
  const [buyOpen, setBuyOpen] = useState(false);

  const onLogout = () => {
    navigate('/');
    setTimeout(() => logout(), 50);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-30 border-b border-white/50 dark:border-white/5 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-slate-900/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden md:flex items-center gap-1.5">
          {user ? (
            <>
              <Button
                variant={isActive('/dashboard') ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate('/dashboard')}
                data-testid="nav-dashboard"
              >
                <LayoutDashboard size={16} className="mr-2" /> Dashboard
              </Button>
              <Button
                variant={isActive('/carousel-designer') ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate('/carousel-designer')}
                data-testid="nav-carousel-designer"
              >
                <Wand2 size={16} className="mr-2" /> Designer
              </Button>
              <Button
                variant={isActive('/methodology') ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate('/methodology')}
                data-testid="nav-methodology"
              >
                <BookOpen size={16} className="mr-2" /> Methodology
              </Button>
              <Button
                variant={isActive('/pricing') ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate('/pricing')}
                data-testid="nav-pricing"
              >
                <Tag size={16} className="mr-2" /> Pricing
              </Button>
              <Button
                variant={isActive('/settings') ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate('/settings')}
                data-testid="nav-settings"
              >
                <Settings size={16} className="mr-2" /> Settings
              </Button>
              <Button
                variant={isActive('/preferences') ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate('/preferences')}
                data-testid="nav-preferences"
                title="Expert-audited preference questionnaire"
              >
                <ClipboardList size={16} className="mr-2" /> Preferences
              </Button>
              {(user.role === 'admin' || user.role === 'editor'
                || (Array.isArray(user.admin_features) && user.admin_features.length > 0)) && (
                <Button
                  variant={isActive('/admin') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => navigate('/admin')}
                  data-testid="nav-admin"
                >
                  <ShieldCheck size={16} className="mr-2" />
                  {user.role === 'admin'
                    ? 'Admin'
                    : user.role === 'editor'
                      ? 'My audits'
                      : 'Tools'}
                </Button>
              )}
            </>
          ) : (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={location.pathname.startsWith('/features') ? 'secondary' : 'ghost'}
                    size="sm"
                    data-testid="nav-features-trigger"
                    className="gap-1"
                  >
                    <BookOpen size={16} className="mr-1.5" />
                    Features
                    <ChevronDown size={14} className="opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[300px]" data-testid="nav-features-menu">
                  <DropdownMenuLabel className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">
                    What Blayzrr does
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/features/profile-success')}
                    className="flex items-start gap-3 py-2.5 cursor-pointer"
                    data-testid="nav-features-profile-success"
                  >
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
                      <Sparkles size={16} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13.5px] font-semibold text-foreground leading-tight">LinkedIn Profile Success</span>
                      <span className="block text-[11.5px] text-muted-foreground leading-snug mt-0.5">
                        Audit, score & rewrite your profile so recruiters and buyers actually stop scrolling.
                      </span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/features/thought-leadership')}
                    className="flex items-start gap-3 py-2.5 cursor-pointer"
                    data-testid="nav-features-thought-leadership"
                  >
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      <Rocket size={16} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13.5px] font-semibold text-foreground leading-tight">LinkedIn Executive Thought Leadership</span>
                      <span className="block text-[11.5px] text-muted-foreground leading-snug mt-0.5">
                        Monthly posts written in your voice, ready to publish — with a built-in strategy behind every one.
                      </span>
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant={isActive('/pricing') ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate('/pricing')}
                data-testid="nav-pricing"
              >
                <Tag size={16} className="mr-2" /> Pricing
              </Button>
            </>
          )}
        </nav>
        <div className="flex items-center gap-2">
          {user && <QuotaPill />}
          <ThemeToggle />
          {user ? (
            <>
              <span className="hidden sm:inline text-sm text-muted-foreground" data-testid="header-user-email">{user.email}</span>
              <Button variant="ghost" size="icon" onClick={onLogout} aria-label="Logout" data-testid="header-logout-button">
                <LogOut size={18} />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="header-login-button">
                Log in
              </Button>
              <Button size="sm" onClick={() => setBuyOpen(true)} data-testid="header-buy-now-button">
                Buy Now
              </Button>
            </>
          )}
        </div>
      </div>
      <GuestCheckoutDialog open={buyOpen} onOpenChange={setBuyOpen} />
    </header>
  );
}

import { SiteFooter } from './SiteFooter';

// Route prefixes that are the "app" (logged-in) surface. These get a
// clean neutral background (#F7F8FC) with no decorative gradient blobs
// so premium audit UI reads calm and product-forward. Everything else
// (landing / pricing / marketing) keeps the pastel marketing shell.
const APP_ROUTE_PREFIXES = [
  '/dashboard',
  '/wizard',
  '/analysis',
  '/carousel-designer',
  '/settings',
  '/preferences',
  '/billing',
  '/admin',
];

function isAppRoute(pathname) {
  return APP_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function AppShell({ children, variant }) {
  const location = useLocation();
  // Explicit prop wins; otherwise auto-detect from the route so
  // logged-in pages get the neutral shell without touching each page.
  const isApp = variant === 'app' || (variant !== 'marketing' && isAppRoute(location.pathname));
  return (
    <div
      className={`min-h-screen text-foreground flex flex-col ${
        isApp
          ? 'bg-[#F7F8FC] dark:bg-slate-950'
          : 'bg-background dark:bg-slate-950'
      }`}
    >
      {/* Marketing shell background is a flat white — the pastel
          orbs that used to float here were removed on 2026-10-01
          because they fought the Apple-quiet redesign. */}
      <AppHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}
